import _Vue from 'vue';

import { error as errorLog } from '~/util/log';

interface CustomError {
  msg: string;
  category: string;
  stack: string;
}

function logError(error: CustomError) {
  errorLog(`${error.msg} ${error.stack}`, `${error.category}`);
}

function initJsErrorHandler(): void {
  if (!window) {
    return;
  }
  window.onerror = (
    msg: Event | string,
    url?: string,
    line?: number,
    col?: number,
    error?: Error,
  ) => {
    const info = {
      msg: msg.toString(),
      category: 'js error',
      stack: error && error.stack ? error.stack.toString() : '',
    };
    logError(info);
    return true;
  };
}
function initResourceErrorHandler(): void {
  if (!window) {
    return;
  }
  window.addEventListener(
    'error',
    e => {
      const { target } = e;
      const info = {
        msg: `${target ? target.localName : 'resource'} is load error`,
        stack: `${target ? target.currentSrc : ''}`,
        category: 'resource',
      };
      if (e.target !== window) {
        // 抛去js语法错误
        logError(info);
      }
    },
    true,
  );
}
function initPromiseErrorHandler(): void {
  if (!window) {
    return;
  }
  window.addEventListener('unhandledrejection', e => {
    e.preventDefault();
    const { reason } = e;
    if (reason.type === 'fetch') {
      return true;
    }
    const info = {
      msg: reason.message || reason,
      category: 'promise',
      stack: reason.stack ? reason.stack : '',
    };
    logError(info);
    return true;
  });
}
function initAjaxErrorHandler(): void {
  // 重写send方法,监控xhr请求
  const ajaxListener = {
    tempSend: XMLHttpRequest.prototype.send, // 复制原先的send方法
    tempOpen: XMLHttpRequest.prototype.open,
  };
  // 重写open方法,记录请求的url
  XMLHttpRequest.prototype.open = function openAjax(
    method: string,
    url: string,
    async: boolean = true,
    user?: string,
    password?: string,
  ) {
    ajaxListener.tempOpen.apply(this, [method, url, async, user, password]);
    this.ajaxUrl = url;
  };
  XMLHttpRequest.prototype.send = function sendAjax(data) {
    ajaxListener.tempSend.apply(this, [data]);
    this.onreadystatechange = function readystatechange(): void {
      if (this.readyState === 4) {
        if (this.status >= 200 && this.status < 300) {
          return;
        }
        const info = {
          msg: `${this.ajaxUrl}:${this.status}`,
          category: 'ajax',
          stack: this.statusText,
        };
        logError(info);
      }
    };
  };
}

function initFetchErrorHandler(): void {
  const originFetch = window.fetch;
  function checkStatus(response: Response) {
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    const error = new Error(`${response.status}:${response.statusText}`);
    throw error;
  }
  window.fetch = function fetch(...args) {
    return new Promise((resolve, reject) => {
      originFetch
        .apply(this, args)
        .then(checkStatus)
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          const logInfo = {
            msg: `${error.messsage ? error.messsage : ''}`,
            stack: `${error.stack ? error.stack : ''}`,
            category: 'fetch',
          };
          error.type = 'fetch';
          logError(logInfo);
          reject(error);
        });
    });
  };
}
export default {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  install: (Vue: typeof _Vue): void => {
    // 初始化js错误监控
    initJsErrorHandler();
    // 初始化资源错误监控
    initResourceErrorHandler();
    // 初始化promise错误监控
    initPromiseErrorHandler();
    // 初始化ajax错误监控
    initAjaxErrorHandler();
    // 初始化fetch监控
    initFetchErrorHandler();
    // 初始化Vue错误监控
    Vue.config.errorHandler = (err, vm, info) => {
      const logInfo = {
        msg: '',
        stack: `${err}`,
        category: `vue:${info}`,
      };
      logError(logInfo);
    };
  },
};
